import React from 'react';
import { Square, Search, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Download, Check } from 'lucide-react';
import RedactionReasonSelect from './RedactionReasonSelect';

const RedactionToolbar = ({
  currentPage,
  totalPages,
  zoom,
  activeTool,
  activeReasonCode,
  reasonCodes,
  markCount,
  onPageChange,
  onZoomChange,
  onToolChange,
  onReasonChange,
  onAutoDetect,
  onApply,
  onDownload,
  isCompleted,
}) => {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-gray-200 flex-wrap">
      {/* Left: tools */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onToolChange(activeTool === 'draw' ? null : 'draw')}
          className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded ${
            activeTool === 'draw'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          title="Draw redaction rectangle"
        >
          <Square className="h-4 w-4" />
          Draw
        </button>

        <button
          onClick={onAutoDetect}
          className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded"
          title="Auto-detect sensitive information"
        >
          <Search className="h-4 w-4" />
          Auto-Detect
        </button>

        <div className="h-5 w-px bg-gray-300" />

        <span className="text-xs text-gray-500 mr-1">Reason:</span>
        <RedactionReasonSelect
          value={activeReasonCode}
          onChange={onReasonChange}
          reasonCodes={reasonCodes}
        />
      </div>

      {/* Center: page navigation */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm text-gray-700 min-w-[80px] text-center">
          Page {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="p-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Right: zoom + actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onZoomChange(Math.max(0.5, zoom - 0.25))}
          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="text-xs text-gray-600 min-w-[40px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => onZoomChange(Math.min(3, zoom + 0.25))}
          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4" />
        </button>

        <div className="h-5 w-px bg-gray-300" />

        <button
          onClick={onApply}
          disabled={markCount === 0}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          {isCompleted ? `Re-Apply (${markCount})` : `Apply (${markCount})`}
        </button>
        {isCompleted && (
          <button
            onClick={onDownload}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        )}
      </div>
    </div>
  );
};

export default RedactionToolbar;
